import React, { useEffect, useState, useCallback } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro } from "@/admin/ui";
import { AudiencePicker, audienceText } from "@/admin/AudiencePicker";
import { fmtTime, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Plus, Clock, MapPin, Shirt, Users, MoreVertical, Pencil, Copy, Send, Ban,
  Archive, Trash2, Crown, Lock, Navigation, ArchiveRestore,
} from "lucide-react";

const STATUS_STYLE = {
  published: "bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))]",
  draft: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
  cancelled: "bg-[hsl(var(--pc-gold-2)/0.18)] text-[hsl(var(--pc-gold-2))]",
  archived: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] line-through",
};

const _MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "2026-12-09" -> "Dec 9" (string parse; no timezone shift). Display label only.
const mdShort = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  const p = iso.split("-");
  if (p.length < 3) return iso;
  const m = Number(p[1]); const d = Number(p[2]);
  return _MONTHS[m - 1] ? `${_MONTHS[m - 1]} ${d}` : iso;
};

const blank = () => ({
  title: "", date: "2026-12-11", start_time: "18:00", end_time: "19:00", venue: "",
  description: "", meeting_point: "", dress_code: "", guest_policy: "Plus-ones welcome",
  visibility: "ATTENDEE_VISIBLE", status: "draft", plus_one_eligible: true,
  audience_spec: { type: "EVERYONE" },
});

export default function ScheduleAdmin() {
  const { eventId } = useAdminEvent();
  const [sessions, setSessions] = useState([]);
  const [headcounts, setHeadcounts] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.mgrSchedule(eventId).then((r) => setSessions(r.sessions || [])).catch(() => {});
    api.mgrScheduleHeadcounts(eventId)
      .then((r) => setHeadcounts(Object.fromEntries((r.sessions || []).map((s) => [s.id, s]))))
      .catch(() => {});
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const filtered = (vis) => sessions.filter((s) => (s.visibility || "ATTENDEE_VISIBLE") === vis && (showArchived || s.status !== "archived"));
  const byDay = (list) => {
    const days = {};
    list.forEach((s) => { const d = s.day || 1; (days[d] = days[d] || []).push(s); });
    return Object.entries(days).sort((a, b) => a[0] - b[0]);
  };

  const Row = ({ s }) => (
    <div className="premium-card p-4" data-testid={`session-row-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wide font-bold rounded-full px-2 py-0.5 ${STATUS_STYLE[s.status] || STATUS_STYLE.draft}`}>{s.status || "draft"}</span>
            {s.visibility === "INTERNAL_OPERATIONS" && <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold rounded-full px-2 py-0.5 bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))]"><Lock className="h-3 w-3" /> Internal</span>}
            <h4 className="font-semibold text-[hsl(var(--primary))] truncate">{s.title}</h4>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[hsl(var(--muted-foreground))]">
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(s.date).replace(",", "")} · {s.all_day ? "All day" : `${fmtTime(s.start_time)}–${fmtTime(s.end_time)}`}</span>
            {s.provisional_times && !s.all_day && <span data-testid={`session-provisional-${s.id}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold bg-[hsl(var(--pc-gold-2)/0.18)] text-[hsl(var(--pc-gold-2))]">Provisional time · confirm before publish</span>}
            {s.venue && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.venue}</span>}
            {s.dress_code && <span className="inline-flex items-center gap-1"><Shirt className="h-3 w-3" /> {s.dress_code}</span>}
            {s.meeting_point && <span className="inline-flex items-center gap-1"><Navigation className="h-3 w-3" /> {s.meeting_point}</span>}
          </div>
          {s.visibility === "ATTENDEE_VISIBLE" && (() => {
            const hc = headcounts[s.id];
            const cnt = hc?.primary_attendees ?? s.audience_count;
            // Date-driven sessions show a resolved-date label instead of a
            // misleading recognition/type label (display only — resolver unchanged).
            let audLabel = s.audience_label;
            if (hc?.checkin_date_rule) audLabel = `Resolved arrivals on ${mdShort(hc.date)}`;
            else if (hc?.inhouse_after_rule) audLabel = `Still in-house on ${mdShort(hc.date)}`;
            else if (hc?.checkout_date_rule) audLabel = `Checkout ${mdShort(hc.date)}`;
            // Guest-policy label follows the canonical guest mode.
            const gm = hc?.guest_mode;
            const guestLabel = gm === "yes" ? "Guests welcome"
              : gm === "follow" ? "Guests follow attendee"
              : gm === "no" ? "Attendee only"
              : (s.plus_one_eligible ? "Guests welcome" : "Attendee only");
            return (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span data-testid={`session-audience-${s.id}`} className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--pc-gold)/0.14)] text-[hsl(var(--primary))] border border-[hsl(var(--pc-gold)/0.4)] px-2.5 py-1 text-xs font-medium"><Users className="h-3 w-3" /> {audLabel}{cnt != null ? ` · ${cnt} eligible` : ""}</span>
                <span data-testid={`session-guestpolicy-${s.id}`} className="text-xs text-[hsl(var(--muted-foreground))]">{guestLabel}</span>
              </div>
            );
          })()}
          {s.visibility === "ATTENDEE_VISIBLE" && headcounts[s.id] && (
            <div data-testid={`session-headcount-${s.id}`} className="mt-2 inline-flex flex-wrap items-center gap-1 rounded-lg bg-[hsl(var(--secondary)/0.08)] border border-[hsl(var(--secondary)/0.25)] px-2.5 py-1 text-xs text-[hsl(var(--primary))]">
              <span className="font-semibold">{headcounts[s.id].primary_attendees}</span> attendees
              <span className="opacity-60">·</span>
              <span className="font-semibold">{headcounts[s.id].plus_ones}</span> guests
              <span className="opacity-60">·</span>
              <span className="font-semibold text-[hsl(var(--secondary))]">{headcounts[s.id].total_headcount}</span> total
              <span className="ml-1 text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Manager only</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="outline" data-testid={`session-edit-${s.id}`} onClick={() => setEditing(s)}><Pencil className="h-3.5 w-3.5" /></Button>
          {s.status !== "published" && s.status !== "archived" && (
            <Button size="sm" data-testid={`session-publish-${s.id}`} onClick={() => act(() => api.mgrSessionPublish(eventId, s.id), "Published")}><Send className="h-3.5 w-3.5 mr-1" /> Publish</Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button size="sm" variant="outline" data-testid={`session-more-${s.id}`}><MoreVertical className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => act(() => api.mgrSessionDuplicate(eventId, s.id), "Duplicated")}><Copy className="h-4 w-4 mr-2" /> Duplicate</DropdownMenuItem>
              {s.status !== "cancelled" && <DropdownMenuItem onClick={() => act(() => api.mgrSessionCancel(eventId, s.id), "Cancelled")}><Ban className="h-4 w-4 mr-2" /> Cancel</DropdownMenuItem>}
              {s.status !== "archived"
                ? <DropdownMenuItem onClick={() => act(() => api.mgrSessionArchive(eventId, s.id, true), "Archived")}><Archive className="h-4 w-4 mr-2" /> Archive (keep history)</DropdownMenuItem>
                : <DropdownMenuItem onClick={() => act(() => api.mgrSessionArchive(eventId, s.id, false), "Restored")}><ArchiveRestore className="h-4 w-4 mr-2" /> Restore</DropdownMenuItem>}
              {s.status === "draft" && !s.published_ever && <DropdownMenuItem className="text-red-600" onClick={() => act(() => api.mgrSessionDelete(eventId, s.id), "Deleted")}><Trash2 className="h-4 w-4 mr-2" /> Delete draft</DropdownMenuItem>}
              {s.published_ever && <div className="px-2 py-1.5 text-[10px] text-[hsl(var(--muted-foreground))] max-w-[13rem]">Published sessions can't be deleted — archive removes them from the active schedule while preserving history.</div>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );

  const DayList = ({ vis }) => {
    const groups = byDay(filtered(vis));
    if (!groups.length) return <div className="premium-card p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No sessions yet.</div>;
    return (
      <div className="space-y-5">
        {groups.map(([day, list]) => (
          <div key={day}>
            <div className="text-[11px] uppercase tracking-wide font-bold text-[hsl(var(--pc-gold))] mb-2">Day {day} · {fmtDate(list[0].date).replace(",", "")}</div>
            <div className="space-y-2">{list.map((s) => <Row key={s.id} s={s} />)}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <PageIntro>Own the full agenda. Attendee-visible sessions publish live to the app; internal setup and rehearsal items stay in Events Manager only. Audience is resolved by the shared engine — individual Allow/Deny overrides on the attendee record always win.</PageIntro>
        <Button data-testid="add-session-btn" onClick={() => setEditing(blank())}><Plus className="h-4 w-4 mr-1" /> Add Session</Button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Switch id="arch" checked={showArchived} onCheckedChange={setShowArchived} data-testid="toggle-archived" />
        <Label htmlFor="arch" className="text-sm text-[hsl(var(--muted-foreground))]">Show archived</Label>
      </div>

      <Tabs defaultValue="ATTENDEE_VISIBLE">
        <TabsList className="mb-4">
          <TabsTrigger value="ATTENDEE_VISIBLE" data-testid="tab-attendee-visible">Attendee Schedule</TabsTrigger>
          <TabsTrigger value="INTERNAL_OPERATIONS" data-testid="tab-internal-ops"><Lock className="h-3.5 w-3.5 mr-1" /> Internal Operations</TabsTrigger>
        </TabsList>
        <TabsContent value="ATTENDEE_VISIBLE"><DayList vis="ATTENDEE_VISIBLE" /></TabsContent>
        <TabsContent value="INTERNAL_OPERATIONS"><DayList vis="INTERNAL_OPERATIONS" /></TabsContent>
      </Tabs>

      {editing && <SessionEditor eventId={eventId} session={editing} headcount={editing.id ? headcounts[editing.id] : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function SessionEditor({ eventId, session, headcount, onClose, onSaved }) {
  const isNew = !session.id;
  const [f, setF] = useState({ ...blank(), ...session });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const body = {
        title: f.title, date: f.date, start_time: f.start_time, end_time: f.end_time,
        venue: f.venue, description: f.description, meeting_point: f.meeting_point,
        dress_code: f.dress_code, guest_policy: f.guest_policy, visibility: f.visibility,
        plus_one_eligible: f.plus_one_eligible, audience_spec: f.audience_spec,
        all_day: !!f.all_day,
      };
      if (isNew) await api.mgrSessionCreate(eventId, body);
      else await api.mgrSessionUpdate(eventId, session.id, body);
      toast.success(isNew ? "Session created" : "Session updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="session-editor">
        <DialogHeader><DialogTitle>{isNew ? "Add Session" : "Edit Session"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="sess-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Welcome Reception" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div><Label>Start</Label><Input type="time" value={f.start_time || ""} disabled={!!f.all_day} onChange={(e) => set("start_time", e.target.value)} /></div>
            <div><Label>End</Label><Input type="time" value={f.end_time || ""} disabled={!!f.all_day} onChange={(e) => set("end_time", e.target.value)} /></div>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="allday" data-testid="sess-all-day" checked={!!f.all_day} onCheckedChange={(v) => set("all_day", v)} />
            <Label htmlFor="allday" className="text-sm">All-day item (no start/end time · shows "All day")</Label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Venue</Label><Input value={f.venue} onChange={(e) => set("venue", e.target.value)} /></div>
            <div><Label>Meeting point</Label><Input value={f.meeting_point} onChange={(e) => set("meeting_point", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Dress code</Label><Input value={f.dress_code} onChange={(e) => set("dress_code", e.target.value)} /></div>
            <div><Label>Guest policy</Label><Input value={f.guest_policy} onChange={(e) => set("guest_policy", e.target.value)} /></div>
          </div>
          <div><Label>Description / instructions</Label><Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div>
            <Label>Visibility</Label>
            <Select value={f.visibility} onValueChange={(v) => set("visibility", v)}>
              <SelectTrigger data-testid="sess-visibility"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ATTENDEE_VISIBLE">Attendee-visible</SelectItem>
                <SelectItem value="INTERNAL_OPERATIONS">Internal operations (Manager only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {f.visibility === "ATTENDEE_VISIBLE" && (
            <>
              <div className="flex items-center gap-2">
                <Switch id="po" checked={!!f.plus_one_eligible} onCheckedChange={(v) => set("plus_one_eligible", v)} />
                <Label htmlFor="po" className="text-sm">Guests / plus-ones welcome</Label>
              </div>
              <AudiencePicker eventId={eventId} value={f.audience_spec} onChange={(s) => set("audience_spec", s)} />
              {headcount && (
                <div data-testid="session-editor-headcount" className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] p-3">
                  <div className="text-xs font-semibold text-[hsl(var(--primary))] mb-1.5">Headcount <span className="font-normal text-[hsl(var(--muted-foreground))]">(Manager-only · recalculates after you save audience/guest changes)</span></div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <Line k="Attendees" v={headcount.primary_attendees} />
                    <Line k="Guests" v={headcount.plus_ones} />
                    <Line k="Total" v={headcount.total_headcount} strong />
                    <Line k="Expected (RSVP yes)" v={headcount.expected_total} strong />
                    <Line k="Pending potential" v={headcount.pending_total} />
                  </dl>
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="sess-save" onClick={save} disabled={saving}>{saving ? "Saving…" : (isNew ? "Create" : "Save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Line({ k, v, strong }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-[hsl(var(--muted-foreground))]">{k}</dt>
      <dd className={strong ? "font-semibold text-[hsl(var(--secondary))]" : "font-medium text-[hsl(var(--primary))]"}>{v}</dd>
    </div>
  );
}

